import { Button, Grid, GridColumn, Icon, Notice, Table } from 'telia-front-react';
import { inject, observer } from 'mobx-react';
import * as React from 'react';

import AnalyticsService from '../../../services/AnalyticsService';
import { IInvoicesStore } from '../../../stores/InvoicesStore';
import { Invoice } from '../../../stores/models/Invoice';
import { ITranslateStore } from '../../../stores/TranslateStore';
import { formatAsCurrency } from '../../../util/FormatUtil';
import AccentedWithAlert from '../AccentedWithAlert';

import InvoiceDetailsStore from '../../../stores/InvoiceDetailsStore';
import { formatDate } from '../../../util/DateUtil';
import PaidAmountWithSettlementsTooltip from './PaidAmountWithSettlementsTooltip';

interface IPreviousInvoicesTableProps {
  translateStore?: ITranslateStore;
  invoicesStore?: IInvoicesStore;
  invoiceDetailsStore?: InvoiceDetailsStore;
  analyticsService?: AnalyticsService;
}

@inject('invoicesStore', 'translateStore', 'invoiceDetailsStore', 'analyticsService')
@observer
export default class PreviousInvoicesTable extends React.Component<IPreviousInvoicesTableProps> {
  public render(): JSX.Element {
    const translate = this.props.translateStore.translate;
    const invoicesStore = this.props.invoicesStore;

    return (
      <Table responsive="cols" break="md" className="text__margin-double">
        <thead>
          <tr>
            <th>
              <Icon className="hidden-md hidden-lg hidden-xl" modifiers={invoicesStore.previousInvoices.payload[0].iconName} size="sm" />
            </th>
            <th>{translate('invoices.previous-invoices.date')}</th>
            <th>{translate('invoices.previous-invoices.name')}</th>
            <th>{translate('invoices.previous-invoices.total')}</th>
            <th>{translate('invoices.previous-invoices.deadline')}</th>
            <th>{translate('invoices.previous-invoices.paid-amount')}</th>
            <th />
          </tr>
        </thead>

        <tbody>
          {invoicesStore.previousInvoices.payload.map((invoice: Invoice, index: number) =>
            invoice.restricted ? this.restrictedInvoiceRow(invoice, index) : this.invoiceRow(invoice, index)
          )}
        </tbody>
      </Table>
    );
  }

  private restrictedInvoiceRow(invoice: Invoice, index: number) {
    const translate = this.props.translateStore.translate;
    const invoicesStore = this.props.invoicesStore;

    return (
      <tr key={index}>
        <td className="text-center hidden-xs hidden-sm">
          <Icon modifiers={invoice.iconName} size="sm" />
        </td>
        <td>{formatDate(invoice.issuedDate)}</td>
        <td className="text-bold">{invoicesStore.invoiceName(invoice)}</td>
        <td className="c-brand-primary-text text-bold h5">{formatAsCurrency(invoice.amount)}</td>
        <td colSpan={2} data-headless={true}>
          <Notice size="small">{translate('invoices.previous-invoices.restricted-invoice-notice')}</Notice>
        </td>
        <td data-headless={true}>
          <Grid align={['middle-xs', 'end-xs']} verticalGutter="none">
            <GridColumn align={['middle-xs']} width={['xs']}>
              <Button
                text={translate('invoices.previous-invoices.view-invoice')}
                size="small"
                onClick={() => {
                  this.props.invoiceDetailsStore.activateInvoiceDetails(invoice, 'restricted previous invoice');
                }}
              />
            </GridColumn>
          </Grid>
        </td>
      </tr>
    );
  }

  private invoiceRow(invoice: Invoice, index: number) {
    const translate = this.props.translateStore.translate;
    const invoicesStore = this.props.invoicesStore;
    const analyticsService = this.props.analyticsService;

    return (
      <tr key={index}>
        <td className="text-center hidden-xs hidden-sm">
          <Icon modifiers={invoice.iconName} size="sm" />
        </td>
        <td>{formatDate(invoice.issuedDate)}</td>
        <td>{invoicesStore.invoiceName(invoice)}</td>
        <td className="c-brand-primary-text text-bold h5">{formatAsCurrency(invoice.amount)}</td>
        <td>
          <AccentedWithAlert showAlert={invoicesStore.overdue(invoice)}>{formatDate(invoice.dueDate)}</AccentedWithAlert>
        </td>
        <td className="text-bold h5">
          <PaidAmountWithSettlementsTooltip invoice={invoice} id={index} />
        </td>
        <td data-headless={true}>
          <Grid align={['middle-xs', 'end-xs']} verticalGutter="none">
            <GridColumn align={['middle-xs']} width={['xs']}>
              <Button
                text={translate('invoices.previous-invoices.view-invoice')}
                size="small"
                onClick={() => {
                  this.props.invoiceDetailsStore.activateInvoiceDetails(invoice, 'previous invoice');
                  analyticsService.previousInvoicesViewDetails(invoice.contentType, invoice.invoiceNumber);
                }}
              />
            </GridColumn>
          </Grid>
        </td>
      </tr>
    );
  }
}
